/**
 * RouteDateEditor — change a leg's date inline, then re-search the new date with
 * live progress.
 *
 * On save we update the route's departure_date (the backend re-searches ONLY this
 * route, fast) and immediately show a "Searching <new date>…" state, then surface
 * fares progressively as they land — reusing the check-status poll the manual
 * Check-Prices flow already uses. Locked once the flight is held/booked.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Loader2, Check, X } from "lucide-react";
import { routeUpdateApi } from "@/lib/api";
import { useCheckPrices } from "@/hooks/useCheckPrices";

interface Props {
  tripId: string;
  routeId: string;
  date: string | null;
  dateLabel: string | null;
  editable: boolean;
}

function friendly(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export default function RouteDateEditor({ tripId, routeId, date, dateLabel, editable }: Props) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(date ?? "");
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [searchingLabel, setSearchingLabel] = useState<string | null>(null);

  const refetchPlan = () => {
    qc.invalidateQueries({ queryKey: ["tripPlan", tripId] });
    qc.invalidateQueries({ queryKey: ["itinerary", tripId] });
  };

  // Drives the live progress: polls /check-status for the single-route re-check
  // and refetches the plan as fares land. Idle until jobId is set.
  const check = useCheckPrices(tripId, refetchPlan, jobId);

  const mutation = useMutation({
    mutationFn: (newDate: string) => routeUpdateApi.updateDepartureDate(routeId, newDate),
    onSuccess: (res: any, newDate) => {
      setEditing(false);
      setError(null);
      setSearchingLabel(friendly(newDate));
      refetchPlan(); // instant: the new date shows immediately, fares fill in
      if (res?.checkJobId) setJobId(res.checkJobId as string);
    },
    onError: (e: any) => setError(e?.response?.data?.error || e?.message || "Couldn't change the date."),
  });

  const searching = check.phase === "checking" && !!searchingLabel;

  if (!editable) {
    return dateLabel ? <span className="text-sm text-muted-foreground">{dateLabel}</span> : null;
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {!editing ? (
        <button
          type="button"
          onClick={() => { setValue(date ?? ""); setEditing(true); }}
          className="inline-flex items-center gap-1 rounded text-sm text-muted-foreground transition-colors hover:text-foreground"
          title="Change date"
        >
          <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="underline decoration-dotted underline-offset-2">{dateLabel ?? "Set a date"}</span>
        </button>
      ) : (
        <>
          <input
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-7 rounded border border-border bg-background px-1.5 text-sm"
            autoFocus
          />
          <button
            type="button"
            disabled={mutation.isPending || !value || value === date}
            onClick={() => mutation.mutate(value)}
            className="inline-flex h-7 w-7 items-center justify-center rounded bg-[hsl(var(--foreground))] text-white disabled:opacity-50"
            aria-label="Save date"
          >
            {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setError(null); }}
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-border text-muted-foreground"
            aria-label="Cancel"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </>
      )}

      {/* Live search progress for the new date. */}
      {searching && (
        <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))]/[0.1] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--accent-foreground))]">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          {check.status && check.status.flightsFound > 0
            ? `Found ${check.status.flightsFound} so far · ${searchingLabel}`
            : `Searching ${searchingLabel}…`}
        </span>
      )}

      {error && <span className="text-xs text-destructive">{error}</span>}
    </span>
  );
}
